/**
 * Secure Routing Provider - Centralized route security management
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { getNavigationIcon } from '../components/navigation/NavigationIcons';

const SecureRoutingContext = createContext();

export const useSecureRouting = () => {
  const context = useContext(SecureRoutingContext);
  if (!context) {
    throw new Error('useSecureRouting must be used within SecureRoutingProvider');
  }
  return context;
};

// Route configuration with access control
const ROUTE_CONFIG = {
  // Public routes (no authentication required)
  PUBLIC: [
    '/',
    '/programs',
    '/about',
    '/approach',
    '/auth',
    '/login',
    '/password-reset'
  ],

  // Platform admin routes
  PLATFORM_ADMIN: [
    '/admin/platform',
    '/admin/clubs',
    '/admin/clubs/create',
    '/admin/clubs/onboard',
    '/admin/clubs/:id',
    '/admin/clubs/:id/edit',
    '/admin/system'
  ],

  // Club admin routes
  CLUB_ADMIN: [
    '/admin/club',
    '/admin/programs',
    '/admin/users',
    '/admin/batches',
    '/admin/registrations',
    '/admin/reports',
    '/admin/settings'
  ],

  // Coach routes
  COACH: [
    '/coach/dashboard',
    '/coach/evaluations',
    '/coach/batches',
    '/skills',
    '/skills/:playerId'
  ],

  // Parent routes
  PARENT: [
    '/inbox',
    '/documents',
    '/register',
    '/registration-complete'
  ],

  // Player routes (if applicable)
  PLAYER: [
    '/player/dashboard',
    '/player/programs',
    '/player/assessments'
  ],

  // Common authenticated routes
  AUTHENTICATED: [
    '/profile'
  ]
};

export const SecureRoutingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { userRole, hasRole, hasAnyRole } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is accessible for user's role
  const isRouteAccessible = useCallback((pathname) => {
    // Public routes are always accessible
    if (ROUTE_CONFIG.PUBLIC.some(route =>
      route === pathname || pathname.startsWith(route.replace('*', ''))
    )) {
      return true;
    }

    // Must be authenticated for protected routes
    if (!isAuthenticated) {
      return false;
    }

    // Check common authenticated routes
    if (ROUTE_CONFIG.AUTHENTICATED.some(route =>
      route === pathname || pathname.startsWith(route.replace(':id', ''))
    )) {
      return true;
    }

    // Check role-specific routes
    if (hasRole('platform_admin')) {
      return ROUTE_CONFIG.PLATFORM_ADMIN.some(route =>
        pathname === route || pathname.match(route.replace(':id', '[^/]+'))
      ) ||
        ROUTE_CONFIG.CLUB_ADMIN.some(route => pathname.startsWith(route.replace('*', ''))) ||
        ROUTE_CONFIG.COACH.some(route => pathname.startsWith(route.replace('*', ''))) ||
        ROUTE_CONFIG.PARENT.some(route => pathname.startsWith(route.replace('*', '')));
    }

    if (hasRole('club_admin')) {
      return ROUTE_CONFIG.CLUB_ADMIN.some(route =>
        pathname === route || pathname.startsWith(route.replace('*', ''))
      ) ||
        ROUTE_CONFIG.COACH.some(route => pathname.startsWith(route.replace('*', ''))) ||
        ROUTE_CONFIG.PARENT.some(route => pathname.startsWith(route.replace('*', '')));
    }

    if (hasRole('coach')) {
      return ROUTE_CONFIG.COACH.some(route =>
        pathname === route || pathname.startsWith(route.replace('*', ''))
      ) ||
        ROUTE_CONFIG.PARENT.some(route => pathname.startsWith(route.replace('*', '')));
    }

    if (hasRole('parent')) {
      return ROUTE_CONFIG.PARENT.some(route =>
        pathname === route || pathname.startsWith(route.replace('*', ''))
      );
    }

    if (hasRole('player')) {
      return ROUTE_CONFIG.PLAYER.some(route =>
        pathname === route || pathname.startsWith(route.replace('*', ''))
      );
    }

    return false;
  }, [isAuthenticated, hasRole]);

  // Navigate to appropriate landing page based on role
  const navigateToRoleLandingPage = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    switch (userRole) {
      case 'platform_admin':
        navigate('/admin/platform');
        break;
      case 'club_admin':
        navigate('/admin/club');
        break;
      case 'coach':
        navigate('/coach/dashboard');
        break;
      case 'parent':
        navigate('/dashboard');
        break;
      case 'player':
        navigate('/player/dashboard');
        break;
      default:
        navigate('/');
    }
  }, [isAuthenticated, userRole, navigate]);

  // Navigate with permission check
  const secureNavigate = useCallback((path, options = {}) => {
    if (isRouteAccessible(path)) {
      navigate(path, options);
    } else {
      console.warn(`Access denied for route: ${path}`);
      // Navigate to appropriate landing page instead
      navigateToRoleLandingPage();
    }
  }, [isRouteAccessible, navigate, navigateToRoleLandingPage]);

  // Get redirect path for unauthorized access
  const getRedirectPath = useCallback(() => {
    if (!isAuthenticated) {
      return '/auth';
    }

    // Redirect to role-appropriate landing page
    switch (userRole) {
      case 'platform_admin': return '/admin/platform';
      case 'club_admin': return '/admin/club';
      case 'coach': return '/coach/dashboard';
      case 'parent': return '/dashboard';
      case 'player': return '/player/dashboard';
      default: return '/';
    }
  }, [isAuthenticated, userRole]);

  // Check if user can access specific feature
  const canAccessFeature = useCallback((feature) => {
    const featurePermissions = {
      'club-management': ['platform_admin'],
      'user-management': ['platform_admin', 'club_admin'],
      'program-management': ['platform_admin', 'club_admin'],
      'player-evaluation': ['coach', 'club_admin'],
      'skills-assessment': ['coach', 'club_admin'],
      'waiver-management': ['parent', 'club_admin'],
      'registration': ['parent'],
      'reports': ['platform_admin', 'club_admin', 'coach']
    };

    const allowedRoles = featurePermissions[feature];
    if (!allowedRoles) {
      return false;
    }

    return hasAnyRole(allowedRoles);
  }, [hasAnyRole]);

  // Get available navigation items for current user
  const getNavigationItems = useCallback(() => {
    // Icons are now returned as React elements by getNavigationIcon
    // Note: Consumers of this must expect elements, not strings
    const baseItems = [
      { path: '/profile', label: 'Profile', icon: getNavigationIcon('profile') }
    ];

    const roleItems = {
      platform_admin: [
        { path: '/admin/platform', label: 'Platform Dashboard', icon: getNavigationIcon('dashboard') },
        { path: '/admin/clubs', label: 'Club Management', icon: getNavigationIcon('building') },
        { path: '/admin/clubs/onboard', label: 'Onboard Club', icon: getNavigationIcon('rocket') }
      ],
      club_admin: [
        { path: '/admin/club', label: 'Club Dashboard', icon: getNavigationIcon('building') },
        { path: '/admin/programs', label: 'Programs', icon: getNavigationIcon('programs') },
        { path: '/admin/users', label: 'Users', icon: getNavigationIcon('users') },
        { path: '/admin/reports', label: 'Reports', icon: getNavigationIcon('chart') }
      ],
      coach: [
        { path: '/coach/dashboard', label: 'Coach Dashboard', icon: getNavigationIcon('training') },
        { path: '/coach/evaluations', label: 'Player Evaluations', icon: getNavigationIcon('chart') },
        { path: '/skills', label: 'Skills Assessments', icon: getNavigationIcon('soccer') }
      ],
      parent: [
        { path: '/dashboard', label: 'Dashboard', icon: getNavigationIcon('chart') },
        { path: '/documents', label: 'Documents', icon: getNavigationIcon('document') }
      ],
      player: [
        { path: '/player/dashboard', label: 'My Dashboard', icon: getNavigationIcon('chart') },
        { path: '/player/programs', label: 'My Programs', icon: getNavigationIcon('soccer') },
        { path: '/player/assessments', label: 'My Assessments', icon: getNavigationIcon('growth') }
      ]
    };

    return [
      ...baseItems,
      ...(roleItems[userRole] || [])
    ];
  }, [userRole]);

  const value = {
    isRouteAccessible,
    navigateToRoleLandingPage,
    secureNavigate,
    getRedirectPath,
    canAccessFeature,
    getNavigationItems,
    currentPath: location.pathname,
    userRole
  };

  return (
    <SecureRoutingContext.Provider value={value}>
      {children}
    </SecureRoutingContext.Provider>
  );
};